/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {FocusEventHandler, MouseEventHandler} from 'react';
import {
    ErrorType, FetchOptions, GroupedSelectItem, ResponseData, SelectItemShape, SidebarItem, ThemeShape
} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';

import User from '../models/userModel';
import Account from '../models/accountModel';

export interface UserContext {
    user: User | null,
    validatingUser: boolean;
    onChange: (user: User | null, callback?: VoidFunction) => void,
}

export interface AccountContext {
    account: Account | null;
    accounts: Account[];
    onChange: (account: Account | null, callback?: VoidFunction) => void;
    onChangeAccounts: (accounts: Account[], callback?: VoidFunction) => void;
}

export interface ContextShape {
    userContext: UserContext;
    accountContext: AccountContext;
    theme: ThemeShape;
    onChangeContext: (config: UpdateContextConfig, callback?: (context: ContextShape) => void) => void;
    getCurrentRole: () => UserRole | null;
}

export interface UpdateAdminContext {
    user?: User | null;
    account?: Account | null;
}

export interface AdminContextShape {
    activeUser: User | null;
    activeAccount: Account | null;
    onChangeContext: (config: UpdateAdminContext, callback?: (context: AdminContextShape) => void) => void;
}

export interface RegisterErrors {
    firstname: ErrorType;
    lastname: ErrorType;
    email: ErrorType;
    password: ErrorType;
    companyName: ErrorType;
}

export interface CommonAdminErrors {
    firstname: ErrorType;
    lastname: ErrorType;
    email: ErrorType;
    address: ErrorType;
    zipCode: ErrorType;
    city: ErrorType;
    country: ErrorType;
    telNumber: ErrorType;
}

export interface AccountAdminErrors {
    companyName: ErrorType;
    address: ErrorType;
    zipCode: ErrorType;
    city: ErrorType;
    country: ErrorType;
    telNumber: ErrorType;
    email: ErrorType;
    coc: ErrorType;
}

export interface ActivateHandle<T = any> {
    activate: (params?: T, ...otherParams: any) => void;
    close?: VoidFunction;
}

export interface ExportInvoiceFilter {
    country?: string | null;
    reverseChargeOnly?: boolean;
    customerType: CustomerType;
    invoice_field_names?: Array<string>;
    order_field_names?: Array<string>;
    fulfilment?: Fulfilment | null;
}

export interface UserFilter {
    user_id: NullString;
    account_id: NullString;
    customer_id: NullString;
    firstname: NullString;
    lastname: NullString;
    company_name: NullString;
    email: NullString;
}

export interface VatPercentageData {
    total_incl_vat: number;
    total_vat: number;
    total_incl_reversed_vat: number;
    total_unknown_vat: number;
}

export interface TaxTotalsData {
    tax: Record<string, number>;
    preTax: Record<string, number>;
    revenueIncl: Record<string, number>;
    revenueExcl: Record<string, number>;
    reversedIncl: Record<string, number>;
}

export interface CompleteInvoiceFilter extends Record<string, string | null> {
    order_id: string | null;
    date_from: string | null;
    date_to: string | null;
    order_status: OrderStatus | '' | null;
    invoice_type: InvoiceType | '' | null;
    customer_type: CustomerType;
    invoice_num: string | null;
    firstname: string | null;
    lastname: string | null;
    company: string | null;
    reference: string | null;
    vat_number: string | null;
    coc_number: string | null;
    country: string | null;
    status: InvoiceStatus | '' | null;
    ean: string | null;
}

export interface InvoiceUploadRule {
    id: string;
    countries: Array<string> | null;
    customer_type: CustomerType;
    rule_type: InvoiceRuleType;
}

export interface ReviewNotificationRule {
    id: string;
    product: Array<string> | null;
    isGreater: string;
    rating: number;
    email: string;
}

export interface SearchVolumeData {
    search_term: string;
    periods: Array<SearchVolumePeriod>;
    country_totals: Record<string, number>;
}

export interface SearchVolumePeriod {
    period: string;
    NL: number;
    BE: number;
    total: number;
}

export interface BolSearchTerm {
    search_term: string;
    total: number;
}

export interface BolmateSidebarItem extends SidebarItem {
    index: number;
    alwaysOpen?: boolean;
    onClick?: MouseEventHandler;
    children?: Array<BolmateSidebarItem>;
    parentId?: string;
    disabled?: boolean;
    subText?: string;
    accessKey: 'allow' | 'deny' | AccessKey | Array<AccessKey>;
    authenticator?: (user: User | null, account: Account | null) => boolean;
}

export interface ReviewProduct extends Record<string, any> {
    image_url: string;
    title: string;
    ean: string;
    global_id: string;
    rating: number;
    nps_score: number;
}

export interface ReviewTableProduct {
    ean: string;
    title: string;
    image_url: string;
    total_reviews: number;
    score_rating: number;
    total_negative: number;
    total_positive: number;
    period_total: number;
    period_negative: number;
    period_positive: number;
    nps_score: number;
    period_nps_score: number;
}

export interface SalesDashboardProduct {
    ean: string;
    image_url: string;
    title: string;
    revenue: number;
    revenue_incl: number;
    quantity: number;
    num_orders: number;
    avg_price: number;
    avg_price_incl: number;
    commission_percentage: number;
    avg_commission: number;
    commission: number;
    has_product_costs: boolean;
    costs: number;
    profit: number;
    profit_per_product: number;
    margin: number;
    global_id: string | null;
    returned_quantity: number;
    vat_percentage: Record<string, DefaultVATValue>;
}

export interface ProductCosts {
    id: string | null;
    ean: string;
    start_date: string;
    end_date: string | null;
    fulfilment: Fulfilment;
    cost_price: number;
    fulfilment_cost: number;
    send_cost: number;
    send_cost_type: SendCostType;
    misc_costs_per_item: Record<string, number>;
    misc_costs_per_order: Record<string, number>;
    miscCostsPerItem: Array<{ label: string; value: number }>;
    miscCostsPerOrder: Array<{ label: string; value: number }>;
    internalID: string;
}

export interface FBBItem extends GroupedSelectItem {
    fulfilmentCosts: number;
    sendCosts: number;
}

export interface BasicMessage {
    id: string;
    messageType?: SystemMessageType;
}

export interface Churn {
    period: string;
    registered: number;
    trialing: number;
    percentage_trialing: number;
    free: number;
    percentage_free: number;
    active: number;
    percentage_active: number;
    canceled: number;
    percentage_canceled: number;
    expired: number;
    percentage_expired: number;
    inactive: number;
    percentage_inactive: number;
}

export interface RequestOptions extends FetchOptions {
    maintenanceAllowed?: boolean;  // If the request should be sent in maintenance mode
}

export interface StripeTaxID {
    id: string;
    value: string;
    verification: {
        status: StripeVerificationStatus;
        verified_address: string | null;
        verified_name: string | null;
    } | null;
}

export interface StripeAddress {
    city: string | null;
    country: string | null;
    line1: string | null;
    line2: string | null;
    postal_code: string | null;
}

export interface StripeCustomer {
    name: string | null;
    email: string | null;
    phone: string | null;
    address: StripeAddress;
}

export interface VATValue {
    vat_type: VATType;
    value: any;
}

export interface DefaultVATValue extends VATValue {
    vat_type: VATType.DEFAULT;
    value: number;
}

export interface ReversedVATValue extends VATValue {
    vat_type: VATType.REVERSED;
    value: null;
}

export interface MarginVATValue extends VATValue {
    vat_type: VATType.MARGIN;
    value: null;
}

export interface KORVATValue extends VATValue {
    vat_type: VATType.KOR;
    value: null;
}

export interface SplitVATValue extends VATValue {
    vat_type: VATType.SPLIT;
    value: Record<string, number>;
}

export interface CustomVATValue extends VATValue {
    vat_type: VATType.CUSTOM;
    value: number;
}

export interface NotSetVATValue extends VATValue {
    vat_type: VATType.NOT_SET;
    value: null;
}

export interface BolmateSelectItem<RT = any> extends Omit<SelectItemShape, 'value'> {
    value: BolmateSelectItemValue<RT>;
    info?: string;
}

//===========================
// TYPES
//===========================
export type BolmateSelectItemValue<RT = any> = string | number | RT;

export type UnlayerMergeTag = {
    name: string;
    value?: string;
    sample?: string;
    icon?: string;
    mergeTags?: UnlayerMergeTags;
    rules?: {
        [key: string]: {
            name: string;
            before: string;
            after: string;
            sample?: boolean | Array<Record<string, string>>;
        };
    };
}

export type UnlayerMergeTags = {
    [key: string]: UnlayerMergeTag;
}
export type NullString = string | null;

export type VatPeriod = { label: string, start: IDate, end: IDate }

export type LoginErrors = {
    email: ErrorType;
    password: ErrorType;
    general: ErrorType;
}

export type InvoiceFilter = Partial<CompleteInvoiceFilter>;

export type AccountStats = {
    mail_count: number;
    mail_count_thirty_days: number;
    order_count: number;
    order_count_thirty_days: number;
    review_count: number;
    review_count_thirty_days: number;
};

export type InvoiceUploadStats = {
    open: number;
    closed: { total: number, thirty_days: number };
    failed: number;
};

export type OrderItemErrors = {
    title: ErrorType;
    quantity: ErrorType;
    price: ErrorType;
}

export type InvoiceSettingsErrors = {
    invoicePrefix: ErrorType;
    invoiceStart: ErrorType;
    oldInvoicePrefix: ErrorType;
    oldInvoiceStart: ErrorType;
}

export type ResetErrorHandler<T> = (key: keyof T) => FocusEventHandler;
export type ResetPasswordErrors = {
    password: ErrorType;
    confirmPassword: ErrorType;
}

export type PasswordErrors = {
    password: ErrorType;
    newPassword: ErrorType;
    confirmPassword: ErrorType;
}

export type HeaderCrumb = {
    label: string;
    route?: string;
}

export type UpdateContextConfig = {
    user?: User | null;
    validatingUser?: boolean;
    account?: Account | null;
    accounts?: Account[];
}

export type ConditionedUpdate = {
    elements: React.ReactNode[] | React.ReactNode,
    userCondition: (user: User | null) => boolean;
}

export type PlatformUpdate = {
    date: IDate;
    title: string;
    updates: Array<ConditionedUpdate | React.ReactNode>;
    userCondition?: (user: User | null) => boolean;
}

export type IdealErrors = {
    name: ErrorType;
    email: ErrorType;
}

export type DashboardPeriod = {
    key: PeriodKey;
    label: string;
    periodStart: IDate | null;
    periodEnd: IDate | null;
}

export type SubscriptionStats = {
    active: number;
    trialing: number;
    free: number;
    canceled: number;
    expired: number;
    inactive: number;
}

export type BolInvoice = {
    invoice_id: string;
    period_start: string;
    period_end: string;
}

export type EmailStepStat = {
    period: string;
    SEND: number;
    OPEN: number;
    CLICK: number;
}

export type EmailStepStatTotal = {
    SENT: number;
    OPEN: number;
    CLICK: number;
}

export type SelectableAccount = {
    id: string;
    label: string;
}

export type ReturnCounts = { [key in ReturnProcessingResult]: number };

export type AccountProgress = {
    api: boolean;
    pdf_layout: boolean;
    mail: boolean;
    vat: boolean;
    upload: boolean;
}

export type SurveyOption = {
    key: string;
    title: string;
    description: string | null;
}

export type ReviewChartData = {
    positive: number;
    negative: number;
}

export type PrimeSorting = 0 | 1 | -1 | null | undefined;

export type ImageSources = Array<[string, string]>;

export type VatCountryData = Record<string, VatPercentageData>;
export type VatDeclarationData = Record<string, VatCountryData>;

export type SelectionUser = {
    id: string;
    firstname: string;
    lastname: string;
    email: string;
    subscription_created_at: string;
    subscription_status: string;
    has_payment_method: boolean;
}

export type EmailStatLoader = { id: string, callback: (data: ResponseData) => void };

export type UserRight = {
    key: AccessKey;
    right_type: UserRightType;
    allow: boolean;
}

export type UserRightItem = Omit<UserRight, 'allow'> & {
    label: string;
    children?: Array<UserRightItem>;
}

export type ErrorResetter<T extends Record<string, ErrorType>> = (key: keyof T) => FocusEventHandler;

export type ApexBarSerie = { name: string; data: Array<number> };

export type UnlayerBodyItem = Array<{
    id: string;
    cells: number[];
    columns: Array<any>;
    values: {};
}>;

export type UnlayerDesign = {
    counters: Record<string, number>;
    readonly body: {
        id: string | undefined;
        rows: UnlayerBodyItem;
        headers: UnlayerBodyItem;
        footers: UnlayerBodyItem;
        values: Record<string, any>;
    };
    schemaVersion?: number;
}

export type InvoiceDataErrors = {
    firstname: ErrorType;
    lastname: ErrorType;
    address: ErrorType;
    houseNumber: ErrorType;
    zipCode: ErrorType;
    city: ErrorType;
    invoiceNumber: ErrorType;
    invoiceNumberPrefix: ErrorType;
}

export type IdleChangeListener = (currentState: IdleState) => void;

export type EmailActivity = {
    date: string;
    action: EmailStepAction;
    flex: Record<string, any> | null;
}
//===========================
// ENUMS
//===========================

export enum UserType {
    ADMIN = 'admin', USER = 'user', PREMIUM = 'premium_user', SUPER_ADMIN = 'super-admin', SUPPORT = 'support'
}

export enum DataMode {
    DETAILS, EDIT
}

export enum EditMode {
    EDIT, NEW
}

export enum CreateAccountStep {
    WELCOME, PERSONAL, COMPANY, ADDITIONAL, INVOICE, FINISHED, REFERRAL, OTHER_TOOLS
}

export enum UserStatus {ACTIVE = 'active', INACTIVE = 'inactive', BLOCKED = 'blocked', INCOMPLETE = 'incomplete'}

export enum InvoiceStatus {
    SENT = 'sent',
    ERROR = 'error',
    IMPORTED = 'imported',
    WARNING = 'warning',
    TRANSPORT = 'transport'
}

export enum SettingsTab {
    COMMON, EMAIL, CONNECTION, INVOICES, PASSWORD, VAT, SUBSCRIPTION, EXPORT,
    ACCOUNT_USERS,
}

export enum CustomerType {PRIVATE = 'PRIVATE', BUSINESS = 'BUSINESS', BOTH = 'BOTH'}

export enum Country {NL = 'Nederland', BE = 'België', DE = 'Germany'}

export enum AccountStatus {IMPORTING = 'importing', IDLE = 'idle', ERROR = 'error', SETUP_API = 'setup_api'}

export enum HistoryAction {
    CREATE = 'create', UPDATE = 'update', DOWNLOAD = 'download', SEND = 'send', SEND_REVIEW = 'send_review',
    SEND_OTHER = 'other_send', SEND_PRODUCT = 'send_product', ORDER_PLACED = 'order_placed', ORDER_SENT = 'order_sent'
}

export enum PagerType {FIRST, PREVIOUS, PAGE, NEXT, LAST}

export enum ThreadStatus {
    RUNNING = 'running',
    SHOULD_STOP = 'should_stop',
    STOPPED = 'stopped',
    FINISHED = 'finished',
    WAITING = 'waiting'
}

export enum ThreadType {
    PERIODIC = 'periodic', HISTORY = 'history', PENDING_SENDER = 'pending_sender',
    HISTORY_SENDER = 'history_sender', MAILCHIMP_SYNC = 'mailchimp_sync', MAILERLITE_SYNC = 'mailerlite_sync',
    PRODUCT_IMPORTER = 'products_import', INVOICE_EXPORT = 'invoice_export', RETURNS_IMPORT = 'import_returns',
    VAT_DECLARATION = 'vat_declaration', EXPORT_VAT_DECLARATION = 'vat_decl_export',
    INVOICE_REQUEST_IMPORTER = 'invoice_request_importer', BOL_PRODUCT_SYNC = 'bol_product_sync',
    RATINGS_IMPORT = 'ratings_import', RATINGS_EXPORT = 'rating_export'
}

export enum SystemServiceName {
    PERIODIC = 'periodic', MAILCHIMP = 'mailchimp_sync', MAILERLITE_SYNC_SERVICE = 'mailerlite_sync',
    HISTORY = 'history_sender', THREAD = 'thread_cleanup', PENDING = 'pending_sender',
    CONNECTION = 'connection_checker', PRODUCT_IMPORTER = 'products_import', EMAIL_SEQUENCE = 'email_sequence',
    RETURN_IMPORT = 'return_import', PLANNED_PROCESS_EXECUTOR = 'planned_process_executor',
    INVOICE_REQUEST_IMPORTER = 'invoice_request_importer', BOL_PRODUCT_SYNC = 'bol_products_sync',
    RATINGS_IMPORTER = 'rating_importer', NOTIFICATION_SENDER = 'notification_sender',
}

export enum SubscriptionStatus {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    FAILURE = 'failure',
    TRIAL = 'trialing',
    CANCELED = 'canceled',
    FREE = 'free',
    UNPAID = 'unpaid',
}

export enum PaymentType {CARD = 'CARD', SEPA = 'SEPA', IDEAL = 'IDEAL'}

export enum ApiKeyStatus {ACTIVE = 'active', IMPORTING = 'importing', INACTIVE = 'inactive', ERROR = 'error'}

export enum InvoiceType {DEBET = 'debet', CREDIT = 'credit'}

export enum InvoiceExportType {
    CSV = 'CSV',
    CSV_CUSTOM = 'CSV_CUSTOM',
    PDF = 'PDF',
    UBL = 'UBL',
    COMBINED_PDF = 'COMBINED_PDF',
    VAT_DECL_CSV = 'VAT_DECL_CSV',
    VAT_DECL_EXCEL = 'VAT_DECL_EXCEL',
}

export enum ExportProcessStatus {EXPORTING = 'exporting', ERROR = 'error', FINISHED = 'finished'}

export enum CountryCode {NL = 'NL', BE = 'BE', DE = 'DE'}

export enum EmailFlow {
    INVOICE = 'INVOICE', REVIEW = 'REVIEW', PRODUCT = 'PRODUCT', OTHER = 'OTHER',
    REVIEW_INVOICE = 'REVIEW_INVOICE', CREDIT_INVOICE = 'CREDIT_INVOICE'
}

export enum OrderStatus {PLACED = 'PLACED', SENT = 'SENT', CANCELED = 'CANCELED'}

export enum PeriodKey {
    ALL = 'all',
    THIRTY_DAYS = 'thirty_days',
    CURRENT_MONTH = 'current_month',
    LAST_MONTH = 'last_month',
    CURRENT_QUARTER = 'current_quarter',
    CURRENT_YEAR = 'current_year',
    CUSTOM = 'custom'
}

export enum DataSplitMethod {
    HOUR = 'HOUR',
    DAY = 'DAY',
    WEEK = 'WEEK',
    MONTH = 'MONTH',
    QUARTER = 'QUARTER',
    YEAR = 'YEAR',
}

export enum VATPeriod {
    MONTH = 'MONTH',
    QUARTER = 'QUARTER',
    YEAR = 'YEAR',
    CUSTOM = 'CUSTOM'
}

export enum SystemMessageType {ERROR = 'ERROR', WARNING = 'WARNING', INFO = 'INFO', CONFIRMATION = 'CONFIRMATION'}

export enum ExportNameAttr {INVOICE_NUMBER = 'INVOICE_NUMBER', ORDER_ID = 'ORDER_ID'}

export enum CancelType {ACCOUNT = 'account', SUBSCRIPTION = 'subscription'}

export enum Fulfilment {FBR = 'FBR', FBB = 'FBB', BOTH = 'BOTH'}

export enum ReturnProcessingResult {ACCEPTED = 'ACCEPTED', REJECTED = 'REJECTED', CANCELED = 'CANCELED', OPEN = 'OPEN'}

export enum ReturnHandlingResult {
    RETURN_RECEIVED = 'RETURN_RECEIVED',
    EXCHANGE_PRODUCT = 'EXCHANGE_PRODUCT',
    RETURN_DOES_NOT_MEET_CONDITIONS = 'RETURN_DOES_NOT_MEET_CONDITIONS',
    REPAIR_PRODUCT = 'REPAIR_PRODUCT',
    CUSTOMER_KEEPS_PRODUCT_PAID = 'CUSTOMER_KEEPS_PRODUCT_PAID',
    STILL_APPROVED = 'STILL_APPROVED',
    CUSTOMER_KEEPS_PRODUCT_FREE_OF_CHARGE = 'CUSTOMER_KEEPS_PRODUCT_FREE_OF_CHARGE',
    RETURN_ITEM_LOST = 'RETURN_ITEM_LOST',
    EXPIRED = 'EXPIRED',
    EXCESSIVE_RETURN = 'EXCESSIVE_RETURN',
    STILL_RECEIVED = 'STILL_RECEIVED',
    CANCELLED_BY_CUSTOMER = 'CANCELLED_BY_CUSTOMER',
    FAILED_TO_CREATE_SHIPPING_LABEL = 'FAILED_TO_CREATE_SHIPPING_LABEL',
    UNDER_INVESTIGATION = 'UNDER_INVESTIGATION'
}

export enum ConnectionStatus {UP = 'UP', DOWN = 'DOWN', UNSTABLE = 'UNSTABLE'}

export enum VATPercentage {
    HIGH = 21,
    NL_LOW = 9,
    ZERO = 0,
    BE_LOW = 6,
    BE_MID = 12,
    DE_LOW = 7,
    DE_HIGH = 19,
    DEFAULT = -1,
    CUSTOM = -2,
    SPLIT = -3,
    MARGIN = -4
}

export enum BolInvoiceItemType {
    COMMISSION = 'COMMISSION', // Commissie
    CORRECTION_COMMISSION = 'CORRECTION_COMMISSION', // Correctie commissie
    TURNOVER = 'TURNOVER', // Verkoopprijs artikel(en), ontvangen van kopers en door Bol door te storten
    TURNOVER_BOL = 'TURNOVER_BOL',
    CORRECTION_TURNOVER = 'CORRECTION_TURNOVER', // Correctie verkoopprijs artikel(en)
    PLAZA_RETURN_SHIPPING_LABEL = 'PLAZA_RETURN_SHIPPING_LABEL', // Bijdrage aan retourzegel(s)
    CORRECTION_PLAZA_RETURN_SHIPPING_LABEL = 'CORRECTION_PLAZA_RETURN_SHIPPING_LABEL',
    DISTRIBUTION_BY_BOLCOM_LABEL = 'DISTRIBUTION_BY_BOLCOM_LABEL', // Verzenden via Bol - verzendzegel
    CORRECTION_DISTRIBUTION_BY_BOLCOM_LABEL = 'CORRECTION_DISTRIBUTION_BY_BOLCOM_LABEL', // Correctie verzenden via Bol - verzendzegel
    DISTRIBUTION_BY_BOLCOM_COLLECTION = 'DISTRIBUTION_BY_BOLCOM_COLLECTION', // Verzenden via Bol - collectie
    CORRECTION_DISTRIBUTION_BY_BOLCOM_COLLECTION = 'CORRECTION_DISTRIBUTION_BY_BOLCOM_COLLECTION', // Correctie verzenden via Bol - collectie
    DISTRIBUTION_BY_BOLCOM_ADDITION = 'DISTRIBUTION_BY_BOLCOM_ADDITION', // Verzenden via Bol - toeslag
    CORRECTION_DISTRIBUTION_BY_BOLCOM_ADDITION = 'CORRECTION_DISTRIBUTION_BY_BOLCOM_ADDITION', // Correctie verzenden via Bol - toeslag
    DISTRIBUTION_BY_BOLCOM_COSTS = 'DISTRIBUTION_BY_BOLCOM_COSTS', // Logistiek via Bol label kosten
    LOGISTICAL_CHARGE = 'LOGISTICAL_CHARGE', // Logistieke kosten
    OUTBOUND = 'OUTBOUND', // Verzendkosten
    CORRECTION_OUTBOUND = 'CORRECTION_OUTBOUND', // Correctie verzendkosten
    PICK_PACK = 'PICK_PACK', // Pick&pack kosten
    CORRECTION_PICK_PACK = 'CORRECTION_PICK_PACK', // Correctie pick&pack kosten
    STOCK = 'STOCK', // Voorraadkosten
    NCK_STOCK = 'NCK_STOCK', // Onverkoopbare voorraadkosten
    COMPENSATION = 'COMPENSATION', // Compensatie
    COMPENSATION_LOST_GOODS = 'COMPENSATION_LOST_GOODS', // Compensatie zoekgeraakte artikel(en)
    CORRECTION_COMPENSATION_LOST_GOODS = 'CORRECTION_COMPENSATION_LOST_GOODS', // Correctie compensatie zoekgeraakte artikel(en)
    FIRST_MILE = 'FIRST_MILE', // Aanleverdienst
    CORRECTION_FIRST_MILE = 'CORRECTION_FIRST_MILE',
    RETAILER_AUTHORIZATION = 'RETAILER_AUTHORIZATION',
    CORRECTION_RETAILER_AUTHORIZATION = 'CORRECTION_RETAILER_AUTHORIZATION',
    SHIPMENT_LABEL = 'SHIPMENT_LABEL', // Bijdrage aan pakketzegel(s)
    CORRECTION_SHIPMENT_LABEL = 'CORRECTION_SHIPMENT_LABEL',
    SHIPMENT_LABEL_POST = 'SHIPMENT_LABEL_POST', // Bijdrage aan pakketzegel(s) Koninklijke PostNL B.V.
    CORRECTION_SHIPMENT_LABEL_POST = 'CORRECTION_SHIPMENT_LABEL_POST', // Correctie bijdrage aan pakketzegel(s) Koninklijke PostNL B.V.
    BRMG = 'BRMG',
    INBOUND_LABELING = 'INBOUND_LABELING', // Logistiek via Bol label kosten
    SPONSORED_PRODUCTS = 'SPONSORED_PRODUCTS', // Sponsored Products
    SPONSORED_PRODUCTS_DISCOUNT = 'SPONSORED_PRODUCTS_DISCOUNT', // Sponsored Products korting
    CORRECTION_SPONSORED_PRODUCTS = 'CORRECTION_SPONSORED_PRODUCTS', // Sponsored Products correctie
    SOCIAL_ADVERTISING = 'SOCIAL_ADVERTISING', // Social Advertising
    DISPLAY_ONSITE = 'DISPLAY_ONSITE', // Display Onsite
    CORRECTION_DISCOUNT_BRMG = 'CORRECTION_DISCOUNT_BRMG', // Correctie/korting Bol Retail Media Groep
    CORRECTIONS = 'CORRECTIONS',
    STOCK_RETURN = 'STOCK_RETURN',
    VIOLATION = 'VIOLATION',
    DELIVER_SERVICE = 'DELIVER_SERVICE',
    LVB = 'LVB',  // Logistiek via bol - algemeen
    CORRECTION_LVB = 'CORRECTION_LVB',  // Correctie logistiek via bol - algemeen
    SEND_BY_BOL = 'SEND_BY_BOL',  // Verzenden via bol - algemeen
    SEND_BY_BOL_SEAL = 'SEND_BY_BOL_SEAL',  // Verzenden via bol - verzendzegel
    SEND_BY_BOL_ADDITION = 'SEND_BY_BOL_ADDITION',  // Verzenden via bol - toeslag
    CORRECTION_SEND_BY_BOL_SEAL = 'CORRECTION_SEND_BY_BOL_SEAL',  // Correctie verzenden via bol - verzendzegel
    CORRECTION_SEND_BY_BOL = 'CORRECTION_SEND_BY_BOL',  // Correctie verzenden via bol - algemeen
    TRANSACTION_CHECKOUT = 'TRANSACTION_CHECKOUT',  // Transactiekosten over orders geplaatst met Checkout via Bol
// Verkoopprijs Checkout via bol artikel(en), ontvangen van kopers en door bol door te storten
    CHECKOUT_SALE = 'CHECKOUT_SALE',
// Correctie verkoopprijs Checkout via bol artikel(en), ontvangen van kopers en door bol door te storten
    CORRECTION_CHECKOUT_SALE = 'CORRECTION_CHECKOUT_SALE',
}

export enum CategoryType {
    COSTS = 'costs', REVENUE = 'revenue'
}

export enum VatDeclarationInvalidation {VAT = 'VAT', API = 'API'}

export enum ProcessingStatus {
    RUNNING = 'RUNNING', WAITING = 'WAITING', FINISHED = 'FINISHED', ERROR = 'ERROR'
}

export enum ProcessType {
    VAT_DECLARATION = 'VAT_DECLARATION',
    HISTORY_IMPORT = 'HISTORY_IMPORT',
    EMAIL_SEQUENCE = 'EMAIL_SEQUENCE',
    RATINGS_EXPORT = 'RATINGS_EXPORT'
}

export enum APIValidationStatus {UNSET, VALIDATING, VALIDATED, ERROR}

export enum FilenameBase {
    INVOICE_NUMBER = 'INVOICE_NUMBER',
    ORDER_ID = 'ORDER_ID',
    COMBINED = 'COMBINED',
}

export enum InvoiceRequestStatus {
    INVOICE_REQUESTED = 'INVOICE_REQUESTED',
    INVOICE_INCORRECT = 'INVOICE_INCORRECT',
    INVOICE_UPLOADED = 'INVOICE_UPLOADED',
    INVOICE_VIRUS_DETECTED = 'INVOICE_VIRUS_DETECTED',
    FINISHED = 'FINISHED',
    UPLOADING = 'UPLOADING'
}

export enum InvoiceUploadStatus {
    OPEN = 'OPEN',
    CLOSED = 'CLOSED',
    FAILED = 'FAILED'
}

export enum InvoiceUploadType {
    MANUAL = 'MANUAL',
    PROACTIVE = 'PROACTIVE',
    AUTO_RESPOND = 'AUTO_RESPOND',
}

export enum InvoiceRuleType {
    BLACKLIST = 'BLACKLIST',
    WHITELIST = 'WHITELIST'
}

export enum InvoiceRequestsTab {
    REQUESTS, SETTINGS
}

export enum DiscountType {
    PERCENT = 'PERCENT', FIXED_AMOUNT = 'FIXED_AMOUNT'
}

export enum ReviewTab {
    PRODUCTS, SETTINGS, FEED, EXPORT
}

export enum BolReviewSorting {
    MAX_STARS = 'MAX_STARS',
    MIN_STARS = 'MIN_STARS',
    MOST_RECENT = 'MOST_RECENT',
    MOST_USEFUL = 'MOST_USEFUL',
}

export enum BolReviewTableSorting {
    TITLE = 'title',
    TOTAL = 'total',
    POSITIVE = 'total_positive',
    NEGATIVE = 'total_negative',
    PERIOD_TOTAL = 'period_total',
    PERIOD_POSITIVE = 'period_positive',
    PERIOD_NEGATIVE = 'period_negative',
    SCORE_RATING = 'rating',
    NPS_SCORE = 'nps',
    PERIOD_NPS_SCORE = 'period_nps',
}

export enum OrderDirection {
    ASC = 'ASC',
    DESC = 'DESC'
}

export enum AutoUploadType {NONE = 'NONE', BOTH = 'BOTH', PROACTIVE = 'PROACTIVE', AUTO_RESPOND = 'AUTO_RESPOND'}

export enum PaymentFailureType {
    DISPUTE = 'DISPUTE',
    FAILED = 'FAILED',
    BLOCKED = 'BLOCKED'
}

export enum PaymentFailureStatus {
    NEW = 'NEW',
    GENERATED = 'GENERATED',
    SENT = 'SENT',
    CLOSED = 'CLOSED'
}

export enum ReviewSalesPeriod {
    TOTAL = 'TOTAL', SEVEN_DAYS = 'SEVEN_DAYS', THIRTY_DAYS = 'THIRTY_DAYS', CUSTOM = 'CUSTOM'
}

export enum SalesDashboardPeriod {
    TOTAL = 'TOTAL', SEVEN_DAYS = 'SEVEN_DAYS', THIRTY_DAYS = 'THIRTY_DAYS', TODAY = 'TODAY', CUSTOM = 'CUSTOM',
    CURRENT_MONTH = 'CURRENT_MONTH', LAST_MONTH = 'LAST_MONTH', YESTERDAY = 'YESTERDAY'
}

export enum SalesDashboardOrder {
    TITLE = 'TITLE',
    REVENUE = 'REVENUE',
    REVENUE_INCL = 'REVENUE_INCL',
    QUANTITY = 'QUANTITY',
    NUM_ORDERS = 'NUM_ORDERS',
    PRICE = 'PRICE',
    PRICE_INCL = 'PRICE_INCL',
    COMMISSION = 'COMMISSION',
    AVG_COMMISSION = 'AVG_COMMISSION',
    COST_PRICE = 'COST_PRICE',
    COST = 'COST',
    PROFIT = 'PROFIT',
    PROFIT_PER_PRODUCT = 'PROFIT_PER_PRODUCT',
    MARGIN = 'MARGIN',
    COMMISSION_PERCENTAGE = 'COMMISSION_PERCENTAGE',
    RETURNED_QUANTITY = 'RETURNED_QUANTITY',
}

export enum SendCostType {
    PRODUCT = 'PRODUCT', ORDER = 'ORDER'
}

export enum UserRole {
    OWNER = 'OWNER',
    ADMIN = 'ADMIN',
    EMPLOYEE = 'EMPLOYEE',
    CUSTOMER_SERVICE = 'CUSTOMER_SERVICE',
    ACCOUNTANT = 'ACCOUNTANT'
}

export enum UserRightType {
    ACCESS = 'ACCESS',
    ACTION = 'ACTION'
}

export enum RegisterStep {
    METHOD_CHOICE, FORM
}

export enum AccessKey {
// ACCESS
    PAGE_INVOICES = 'page.invoices',
    PAGE_ORDERS = 'page.invoices.orders',
    PAGE_INVOICE_REQUESTS = 'page.invoices.invoice-requests',
    PAGE_RETURNS = 'page.invoices.returns',
    PAGE_INVOICE_LAYOUT = 'page.invoices.layout',
    PAGE_EMAIL = 'page.emails',
    PAGE_NOTIFICATIONS = 'page.notifications',
    PAGE_SALES_DASHBOARD = 'page.sales-dashboard',
    PAGE_REVIEW_ANALYTICS = 'page.review-analytics',
    PAGE_REVIEWS_PRODUCTS = 'page.review-analytics.products',
    PAGE_REVIEWS_FEED = 'page.review-analytics.feed',
    PAGE_REVIEWS_SETTINGS = 'page.review-analytics.settings',
    PAGE_REVIEWS_EXPORT = 'page.review-analytics.export',
    PAGE_VAT_DECLARATION = 'page.vat-declaration',
    PAGE_SEARCH_TERMS = 'page.search-terms',
    PAGE_ACCOUNT_USERS = 'page.account-users',
    PAGE_SETTINGS = 'page.settings',
    PAGE_SETTINGS_API = 'page.settings.api-connection',
    PAGE_SETTINGS_PRODUCTS = 'page.settings.product-settings',
    PAGE_SETTINGS_INVOICE = 'page.settings.invoice-settings',
    PAGE_SETTINGS_EMAILS = 'page.settings.email-settings',
    PAGE_SETTINGS_EXPORT = 'page.settings.export',
    PAGE_SETTINGS_SUBSCRIPTION = 'page.settings.subscription',

// ACTIONS
    INVOICE = 'invoice',
    INVOICE_STATS = 'invoice.stats',
    INVOICE_DETAILS = 'invoice.details',
    INVOICE_EDIT_CUSTOMER = 'invoice.edit.customer',
    INVOICE_EDIT_ITEMS = 'invoice.edit.items',
    INVOICE_DOWNLOAD = 'invoice.download',
    INVOICE_SEND = 'invoice.send',
    INVOICE_CREDIT = 'invoice.credit',
    INVOICE_REVERSE_CHARGE = 'invoice.reverse-charge',
    INVOICE_CANCEL_EMAILS = 'invoice.cancel-mails',
    INVOICE_INVOICE_NUMBER = 'invoice.invoice-number',
    INVOICE_BULK_DOWNLOAD = 'invoice.bulk-download',
    INVOICE_REQUESTS = 'invoice-request',
    INVOICE_REQUEST_UPLOAD = 'invoice-request.upload',
    INVOICE_REQUEST_SETTINGS = 'invoice-request.settings',
    INVOICE_REQUEST_ARCHIVE = 'invoice-request.archive',
    INVOICE_REQUEST_STATS = 'invoice-request.stats',
    RETURN = 'return',
    RETURN_DETAILS = 'return.details',
    RETURN_BOL = 'return.show-in-bol',
    RETURN_SEND_CREDIT = 'return.send-credit',
    RETURN_CREATE_CREDIT = 'return.create-credit',
    EMAIL_EDIT = 'email.edit',
    EMAIL_STATS = 'email.stats',
    EMAIL_EDIT_AUTOMATIC = 'email.edit-automatic',
    EMAIL_TEST_MAIl = 'email.test-mail',
    EMAIL_COPY = 'email.copy',
    SALES_DASHBOARD_COST_PRICES = 'sales-dashboard.set-cost-prices',
    SALES_DASHBOARD_EXPORT = 'sales-dashboard.export',
    RATING_VIEW = 'rating.view',
    RATING_SETTINGS = 'rating.settings',
    RATING_CREATE_EXPORT = 'rating.create-export',
    RATING_DOWNLOAD_EXPORT = 'rating.download-export',
    VAT_DECLARATION_EXPORT = 'vat-declaration.export',
    VAT_DECLARATION_DOWNLOAD_SPEC = 'vat-declaration.download-spec',
    SETTINGS_COMMON = 'common-settings',
    SETTINGS_ACCOUNT_USERS = 'account-user-settings',
    SETTING_EDIT_COMPANY = 'common-settings.edit-company',
    SETTINGS_DELETE_COMPANY = 'common-settings.delete-account',
    API_SETTINGS = 'api-settings',
    API_SETTINGS_DISCONNECT = 'api-settings.disconnect',
    API_SETTINGS_SETUP = 'api-settings.setup',
    PRODUCT_SETTINGS = 'product-settings',
    PRODUCT_SETTINGS_EDIT_TITLE = 'product-settings.edit-title',
    PRODUCT_SETTINGS_EDIT_VAT = 'product-settings.edit-vat',
    PRODUCT_SETTINGS_SYNC = 'product-settings.sync',
    PRODUCT_SETTINGS_UPLOAD = 'product-settings.upload-bulk',
    INVOICE_SETTINGS = 'invoice-settings',
    INVOICE_SETTINGS_EDIT = 'invoice-settings.edit',
    EMAIL_SETTINGS = 'email-settings',
    EMAIL_SETTINGS_EDIT = 'email-settings.edit',
    EXPORT_SETTINGS = 'export-settings',
    EXPORT_SETTINGS_EXPORT = 'export-settings.export',
    EXPORT_SETTINGS_DOWNLOAD = 'export-settings.download',
    ACCOUNT_USERS_INVITE = 'account-users.invite',
    ACCOUNT_USERS_EDIT = 'account-users.edit',
    NOTIFICATIONS_EDIT = 'notifications.edit',
    NOTIFICATIONS_DELETE = 'notifications.delete'
}

export enum ReferralType {
    GOOGLE = 'google',
    SOCIAL = 'social',
    EXISTING_CUSTOMER = 'existing_customer',
    BOL = 'bol',
    OTHER = 'other',
}

export enum SearchVolumePeriodType {
    LAST_WEEK = 'last_week',
    LAST_14_DAYS = 'last_14_days',
    LAST_MONTH = 'last_month',
    LAST_3_MONTHS = 'last_3_months',
    LAST_6_MONTHS = 'last_6_months',
    LAST_YEAR = 'last_year',
    LAST_2_YEARS = 'last_2_years',
}

export enum StripeVerificationStatus {
    PENDING = 'pending', VERIFIED = 'verified', UNVERIFIED = 'unverified', UNAVAILABLE = 'unavailable'
}

export enum VATType {
    DEFAULT = 'DEFAULT',
    REVERSED = 'REVERSED',
    SPLIT = 'SPLIT',
    MARGIN = 'MARGIN',
    KOR = 'KOR',
    // CUSTOM and NOT_SET are frontend only and should never be sent to the backend
    CUSTOM = 'CUSTOM',
    NOT_SET = 'NOT_SET',
}

export enum EmailTip {
    STOCK_PHOTO = 'Vervang de stockfoto: Uit ons onderzoek blijkt dat persoonlijke foto\'s van jou\n of je team zorgen voor een hogere kans op reviews.',
    BOLMATE_LOGO = 'Maak het eigen: Gebruik je bedrijfslogo in plaats van het Bolmate-logo.',
    COMPANY_EMAIL = 'Het is niet toegestaan om een e-mailadres van jouw bedrijf in de mail te plaatsen.',
    HONEST_REVIEW = 'Je gebruikt een knop naar de review pagina, maar verzoekt niet letterlijk om een "eerlijke review".',
    GOLDEN_STAR = 'Het gebruik van ster emojis wordt door bol niet meer toegestaan. Verwijder deze'
}

export enum IdleState {ACTIVE, IDLE}

export enum EmailStepAction {
    ACTIVATE = 'activate',
    DEACTIVATE = 'deactivate'
}

export enum ReviewStatsDisplay {
    FULL, STATS_ONLY
}

export enum ProductReviewAnalyse {
    POSITIVE = 'POSITIVE',
    NEGATIVE = 'NEGATIVE',
    IMPROVEMENTS = 'IMPROVEMENTS',
    TARGET = 'TARGET',
    CUSTOMERS = 'CUSTOMERS',
}

//===========================
// MAPS
//===========================

export const SubscriptionStatusMap: { [key in SubscriptionStatus]: string } = {
    [SubscriptionStatus.ACTIVE]: 'Actief',
    [SubscriptionStatus.INACTIVE]: 'Inactief',
    [SubscriptionStatus.FAILURE]: 'Fout',
    [SubscriptionStatus.TRIAL]: 'Proefperiode',
    [SubscriptionStatus.CANCELED]: 'Gestopt',
    [SubscriptionStatus.FREE]: 'Gratis',
    [SubscriptionStatus.UNPAID]: 'Onbetaald'
};

export const ApiKeyStatusMap: { [key in ApiKeyStatus]: string } = {
    [ApiKeyStatus.ACTIVE]: 'Actief',
    [ApiKeyStatus.ERROR]: 'Fout',
    [ApiKeyStatus.IMPORTING]: 'Importeren',
    [ApiKeyStatus.INACTIVE]: 'Inactief'
};

const today = IDate.now().startOf('day'),
    todayEnd = IDate.now().endOf('day'),
    quarterLastMonth = today.clone().month(getQuarterLastMonth(today.quarter())),
    lastMonth = today.clone().subtract(1, 'month');

export const DashboardPeriods: DashboardPeriod[] = [
    {
        key: PeriodKey.ALL,
        label: 'Alle',
        periodStart: null,
        periodEnd: null
    },
    {
        key: PeriodKey.THIRTY_DAYS,
        label: 'Afgelopen 30 dagen',
        periodStart: today.clone().subtract(30, 'days'),
        periodEnd: todayEnd
    },
    {
        key: PeriodKey.CURRENT_MONTH,
        label: 'Huidige maand',
        periodStart: today.clone().day(1),
        periodEnd: today
    },
    {
        key: PeriodKey.LAST_MONTH,
        label: 'Vorige maand',
        periodStart: lastMonth.clone().day(1),
        periodEnd: lastMonth.clone().day(lastMonth.daysInMonth())
    },
    {
        key: PeriodKey.CURRENT_QUARTER,
        label: 'Huidig kwartaal',
        periodStart: today.clone().set({month: getQuarterFirstMonth(today.quarter()), day: 1}),
        periodEnd: quarterLastMonth.day(quarterLastMonth.daysInMonth())
    },
    {
        key: PeriodKey.CURRENT_YEAR,
        label: 'Huidig jaar',
        periodStart: today.clone().set({month: 1, day: 1}),
        periodEnd: today.clone().set({month: 12, day: 31})
    },
    {
        key: PeriodKey.CUSTOM,
        label: 'Aangepaste periode',
        periodStart: today.clone().subtract(30, 'days'),
        periodEnd: todayEnd
    }
];

function getQuarterLastMonth(quarter: number) {
    return {
        1: 3,
        2: 6,
        3: 9,
        4: 12
    }[quarter]!;
}

function getQuarterFirstMonth(quarter: number) {
    return {
        1: 1,
        2: 4,
        3: 7,
        4: 10
    }[quarter]!;
}

export const ProcessingResultTranslations: { [key in ReturnProcessingResult]: string } = {
    [ReturnProcessingResult.ACCEPTED]: 'Verwerkt',
    [ReturnProcessingResult.OPEN]: 'Openstaand',
    [ReturnProcessingResult.CANCELED]: 'Geannuleerd',
    [ReturnProcessingResult.REJECTED]: 'Geweigerd'
};

export function getProcessingResult(searchValue: string | null): ReturnProcessingResult | null {
    if (!searchValue) return null;
    for (const [key, value] of Object.entries(ProcessingResultTranslations))
        if (value.toLowerCase() === searchValue.toLowerCase()) return key as ReturnProcessingResult;
    return null;
}